/**
 * Modal de solicitação de visualização de tela (Widget)
 * Exibe quando atendente solicita acesso remoto
 * 
 * @author Sistema
 * @version 1.0.0
 */

import React from 'react';
import type { RequestPayload } from '@/hooks/webrtc/types';

interface ScreenShareRequestProps {
  /** Dados da solicitação recebida */
  request: RequestPayload;
  /** Cor primária do widget */
  primaryColor: string;
  /** Callback para aceitar solicitação */
  onAccept: () => void;
  /** Callback para recusar solicitação */
  onReject: () => void;
  /** Se true, está processando resposta */
  isProcessing?: boolean;
}

/**
 * Modal de consentimento para compartilhamento de tela
 * 
 * Exibido quando atendente solicita visualização.
 * Mostra:
 * - Avatar e nome do atendente
 * - Explicação clara do que será compartilhado
 * - Botões de Aceitar/Recusar
 */
export const ScreenShareRequest: React.FC<ScreenShareRequestProps> = ({
  request,
  primaryColor,
  onAccept,
  onReject,
  isProcessing = false,
}) => {
  // Extrai iniciais do nome para fallback do avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Estilos do container principal
  const containerStyles: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    zIndex: 100,
    animation: 'fadeIn 0.2s ease-out',
  };

  // Estilos do card
  const cardStyles: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '320px',
    width: '100%',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.2)',
    animation: 'scaleIn 0.2s ease-out',
  };

  // Estilos do ícone/avatar
  const avatarContainerStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  };

  const avatarStyles: React.CSSProperties = {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: `${primaryColor}15`,
    border: `2px solid ${primaryColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  };

  // Estilos do título
  const titleStyles: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    textAlign: 'center',
    margin: '0 0 8px 0',
  };

  // Estilos da descrição
  const descriptionStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    margin: '0 0 16px 0',
    lineHeight: 1.5,
  };

  // Estilos da lista de garantias
  const listStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#4b5563',
    margin: '0 0 20px 0',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    listStyle: 'none',
  };

  const listItemStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  };

  // Estilos dos botões
  const buttonContainerStyles: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const buttonBaseStyles: React.CSSProperties = {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: isProcessing ? 'wait' : 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    opacity: isProcessing ? 0.7 : 1,
  };

  const rejectButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    backgroundColor: '#f3f4f6',
    color: '#374151',
  };

  const acceptButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    backgroundColor: primaryColor,
    color: '#ffffff',
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}
      </style>

      <div style={containerStyles}>
        <div style={cardStyles}>
          {/* Ícone de monitor */}
          <div style={avatarContainerStyles}>
            <div style={avatarStyles}>
              {request.agentAvatar ? (
                <img 
                  src={request.agentAvatar} 
                  alt={request.agentName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <svg 
                  width="32" 
                  height="32" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke={primaryColor} 
                  strokeWidth="1.5"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              )}
            </div>
          </div>

          {/* Título */}
          <h3 style={titleStyles}>
            Solicitação de Visualização
          </h3>

          {/* Descrição */}
          <p style={descriptionStyles}>
            <strong>{request.agentName}</strong> deseja visualizar sua tela para ajudá-lo melhor.
          </p>

          {/* Lista de garantias */}
          <ul style={listStyles}>
            <li style={listItemStyles}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Somente visualização</span>
            </li>
            <li style={listItemStyles}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Você pode encerrar a qualquer momento</span>
            </li>
            <li style={{ ...listItemStyles, marginBottom: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Nenhum controle sobre seu computador</span>
            </li>
          </ul>

          {/* Botões */}
          <div style={buttonContainerStyles}>
            <button
              style={rejectButtonStyles}
              onClick={onReject}
              disabled={isProcessing}
              onMouseOver={(e) => {
                if (!isProcessing) {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
            >
              Recusar
            </button>
            <button
              style={acceptButtonStyles}
              onClick={onAccept}
              disabled={isProcessing}
              onMouseOver={(e) => {
                if (!isProcessing) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = isProcessing ? '0.7' : '1';
              }}
            >
              {isProcessing ? 'Aguarde...' : 'Aceitar'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
